import { getProposalMarketQuotesConfig } from '@common/utils';
import { BoxItem } from 'box-ui-elements/es';
import pLimit from 'p-limit';
import { useState } from 'react';
import { FileType } from 'enums';
import { useBoolean, useToast } from 'hooks';
import { messages } from 'i18n';
import { extractExtensionFromFile, getFileType, logger } from 'utils';
import { useConvertApi } from 'broker/hooks/useConvertApi';
import InnerPageLayout from 'broker/pages/SubmissionWorkspacePage/components/InnerPage/InnerPageLayout';
import useLoadProposalFiles from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/ProposalFlow/hooks/useLoadProposalFiles';
import { useUiStoreActions } from 'broker/pages/SubmissionWorkspacePage/ui-store/uiStoreProvider';
import EmptyStateScreen from './components/EmptyStateScreen';
import FilesViewer from './components/FilesViewer';
import { ProposalFlowStep, StepProps } from './types';

const FILE_TYPES_TO_REDACT = [FileType.MarketQuote, FileType.RenewalPolicy];

export default function FilesStep({
  onStepChange,
  handleSaveProposal,
  isDirty,
  setIsDirty,
  selectedFiles,
  submission,
}: StepProps) {
  const [isEmptyState, { off: setNotEmpty }] = useBoolean(!submission?.proposal);
  const [isRedacting, setIsRedacting] = useState(false);

  const marketQuotesConfig = getProposalMarketQuotesConfig(submission);
  const [shouldRedactCommission, setShouldRedactCommission] = useState(marketQuotesConfig?.redactCommission ?? false);
  const { redactContent } = useConvertApi();
  const { showToast } = useToast();
  const { setFilesExplorer } = useUiStoreActions();

  const { emptyState, filesStep } = messages.submissionWorkspace.tabs.proposal;

  const onSetNotEmpty = () => {
    setIsDirty(true);
    setNotEmpty();
  };

  useLoadProposalFiles({
    submission,
    isEmptyState,
    setIsDirty,
  });

  const navigateToEmail = async (files: BoxItem[]) => {
    await onStepChange(ProposalFlowStep.Email, {
      subject: submission.proposal?.subject || '',
      body: submission.proposal?.body || '',
      contactIds: submission.proposal?.contactIds || [],
      attachedFiles: files.map((file) => file.id),
    });
  };

  const handleProceedClicked = async () => {
    if (!shouldRedactCommission) {
      await navigateToEmail(selectedFiles);
      return;
    }

    setIsRedacting(true);
    try {
      const filesToRedact = selectedFiles.filter((file) => {
        const fileType = getFileType(file);
        const fileExtension = extractExtensionFromFile(file.name);

        return !!fileType && FILE_TYPES_TO_REDACT.includes(fileType) && fileExtension === 'pdf';
      });

      if (filesToRedact.length === 0) {
        await navigateToEmail(selectedFiles);
        return;
      }

      const limit = pLimit(5);
      const redactedFiles = await Promise.all(
        filesToRedact.map((file) => limit(() => redactContent(file.id, 'the value of the agent commission'))),
      );

      const updatedSelectedFiles = selectedFiles.map((file) => {
        const index = filesToRedact.findIndex((fileToRedact) => fileToRedact.id === file.id);
        if (index !== -1) {
          // replace the selected file with it's redacted version
          return redactedFiles[index];
        }

        return file;
      });

      // Update the selected files in the UI store
      setFilesExplorer({ selectedFiles: updatedSelectedFiles });

      // Navigate to email step with redacted files
      await navigateToEmail(updatedSelectedFiles);

      showToast('success', { message: filesStep.redactSuccessfulMessage });
    } catch (error) {
      showToast('error', { message: filesStep.redactFailedMessage });
      logger.log('error', {
        message: 'Failed to redact commission form files',
        error,
      });
    } finally {
      setIsRedacting(false);
    }
  };

  const beforeNavigation = async () => {
    await handleSaveProposal({
      subject: submission.proposal?.subject || '',
      body: submission.proposal?.body || '',
      contactIds: submission.proposal?.contactIds || [],
      attachedFiles: selectedFiles.map((file) => file.id),
    });
    return true;
  };

  return (
    <InnerPageLayout
      outerDirtyState={{ isDirty, setIsDirty }}
      beforeNavigation={beforeNavigation}
      title={isEmptyState ? emptyState.header : filesStep.header}
      {...(!isEmptyState && {
        footerButtons: {
          cancelButton: (innerPageProps) => ({
            children: messages.buttons.saveForLater,
            loading: false,
            onClick: () => {
              innerPageProps.onClose();
            },
          }),
          proceedButton: () => ({
            children: filesStep.proceedButton,
            loading: isRedacting,
            onClick: handleProceedClicked,
          }),
        },
      })}
    >
      {() =>
        isEmptyState ? (
          <EmptyStateScreen setNotEmpty={onSetNotEmpty} />
        ) : (
          <FilesViewer
            shouldRedactCommission={shouldRedactCommission}
            onRedactCommissionChange={setShouldRedactCommission}
          />
        )
      }
    </InnerPageLayout>
  );
}
